import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { Account, IAccount } from '@services/accounts.service';
import { LevelAsset, AssetsDataService } from '@services/assets-data.service';
import { WebAppUser, AuthenticationService } from '@services/authentication.service';
import { DeviationEvent, EventsDataService } from '@services/events-data.service';
import { RulesDataService } from '@services/rules-data.service';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AssetDistribution, ModelEdit, ModelsService, ModelType } from '@services/models.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { Location } from '@angular/common';
import { convertEpochToDateString } from '@functions/functions';
import { CreateAssetDistributionComponent } from 'src/app/dialogs/create-asset-distribution/create-asset-distribution.component';

/**
 * ModelDetailsComponent
 */
@Component({
	selector: 'app-model-details',
	templateUrl: './model-details.component.html',
	styleUrls: ['./model-details.component.scss']
})
export class ModelDetailsComponent {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** asset */
	public asset: LevelAsset;

	/** deviationFields */
	public deviationFields: Object = { text: 'Message', id: 'DeviationEventID' };

	/** editFields */
	public editFields: Object = { text: 'PreviousProperties', id: 'ModelEditID' };

	/** accountID */
	public accountID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** OrganisationID */
	public OrganisationUID: string;

	/** currentUser */
	public currentUser: WebAppUser;

	/** modelType */
	public modelType: number;

	/** modelID */
	public modelID: number;

	/** model */
	public model: AssetDistribution | any;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[] = [];

	/** assetList */
	public assetList: LevelAsset[] = [];

	/** modelTypes */
	public modelTypes: ModelType[] = [];

	/** modelType */
	public selectedModelType: ModelType;

	/** modelEdits */
	public modelEdits: ModelEdit[] = [];

	/** deviationEvents */
	public deviationEvents: DeviationEvent[] = [];

	/** filteredDeviationEvents */
	public filteredDeviationEvents: DeviationEvent[] = [];

	/**
	 * Default Constructor
	 * @param eventsDataService eventsDataService  
	 * @param assetsDataService assetsDataService 
	 * @param modelsService modelsService 
	 * @param dialog dialog 
	 * @param authenticationService authenticationService  
	 * @param siteSubscriptions siteSubscriptions 
	 * @param router router 
	 * @param route route 
	 * @param globalFunctions globalFunctions  
	 */
	constructor(
		public eventsDataService: EventsDataService,
		public assetsDataService: AssetsDataService,
		public modelsService: ModelsService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		private location: Location
	) {
		this.globalFunctions.displaySpinner(true);
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedOrganisationUID, account, currentUser, params, queryParams, hierarchy]) =>
				!!selectedAccountGUID && !!selectedOrganisationUID && !!account && !!currentUser && !!params && !!queryParams && !!hierarchy)
		).subscribe({
			next: ([selectedAccountGUID, account, selectedOrganisationUID, currentUser, params, queryParams, hierarchy]) => {
				this.accountID = selectedAccountGUID;
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.OrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;
				this.hierarchy = JSON.parse(hierarchy);

				this.assetList = this.flattenAssets(this.hierarchy);

				this.modelID = parseInt(params.get('modelID') ?? '0');
				this.modelType = parseInt(queryParams.get('modelType') ?? '0');

				this.getModelTypes();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});

		this.siteSubscriptions.refreshModelEdits$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((refresh) => !!refresh)
		).subscribe({
			next: () => {
				this.getModelEdits();
				this.siteSubscriptions.RefreshModelEdits(false);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in updateModelList$", false);
			}
		});
	}

	/**
	 * getModel
	 */
	getModel() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		if (this.modelType == 1) {
			this.modelsService.getAssetDistribution$(this.accountID, this.OrganisationUID, 0, this.modelID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((model) => !!model),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.globalFunctions.displaySpinner(false)
				})
			).subscribe({
				next: (model: any) => {
					this.model = <AssetDistribution>model[0];
					let asset = this.assetList.find((x: LevelAsset) => x.AssetID == this.model.AssetID);
					if (asset) {
						this.asset = asset;
					}
					this.getDeviationEvents();
					this.getModelEdits();
				},
			});
		}
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * getModelTypes
	 */
	getModelTypes() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.getModelTypes$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((modelTypes) => !!modelTypes),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false)
			})
		).subscribe({
			next: (modelTypes: any) => {
				this.modelTypes = modelTypes;
				let _type = this.modelTypes.find((x: ModelType) => x.ModelTypeID == this.modelType);
				if (_type) {
					this.selectedModelType = _type;
				}
				this.getModel();
			},
			error: (error) => {
				if (error.status === 404) {

				}
			}
		});
	}

	/** back */
	back() {
		this.location.back();
	}

	/**
	 * getDate
	 * @param timestamp 
	 * @returns 
	 */
	getDate(timestamp: number): string {
		return convertEpochToDateString(timestamp)
	}

	/**
	 * goToAsset
	 */
	goToAsset() {
		this.siteSubscriptions.SetSelectedAsset(this.asset);
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/location/${this.asset.ParentUID}/asset/${this.asset.AssetID}`]);
	}

	/**
	 * goToModels
	 */
	goToModels() {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/models`])
	}

	/**
	 * viewModel
	 */
	viewModel() {
		if (this.selectedModelType.ModelTypeID == 1) {
			let data = {
				asset: this.asset,
				viewing: true,
				distributionID: this.model.AssetDistributionID,
				existingDistribution: this.model
			}

			const dialogRef = this.dialog.open(CreateAssetDistributionComponent
				, { data: data }
			);

			dialogRef.afterClosed().pipe(
				filter(result => !!result)
			).subscribe(result => {
			});
		}
	}

	/**
	 * getModelEdits
	 */
	getModelEdits() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		if (this.selectedModelType.ModelTypeID == 1) {
			this.modelsService.getModelEdits$(this.accountID, this.OrganisationUID, this.selectedModelType.ModelTypeID, this.model.AssetDistributionID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((edits) => !!edits),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (edits) => {
					this.modelEdits = edits;
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.modelsService.getModelEdits$", false);
					}
				}
			});
		}
	}

	/** 
	 * getDeviationEvents
	 */
	getDeviationEvents() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		if (this.selectedModelType.ModelTypeID == 1) {
			this.eventsDataService.getDeviationEvents$(this.accountID, this.OrganisationUID, 0, 0, 0, 0,
				this.selectedModelType.ModelTypeID, this.model.AssetDistributionID).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((events) => !!events),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (events) => {
						this.deviationEvents = events;
						this.filteredDeviationEvents = this.deviationEvents;
					},
					error: (error) => {
						if (error.status != 404) {
							this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getDeviationEvents$", false);
						}
					}
				});
		}
	}

	/**
	 * goToEvent
	 */
	goToEvent(event: DeviationEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${event.DeviationEventID}`], { queryParams: { eventType: 4 } });
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFiltering(event: any): void {
		event.cancel = true;
		const query = event.text.toLowerCase();
		this.filteredDeviationEvents = this.deviationEvents.filter((data: DeviationEvent) => {
			const dateStr = `${convertEpochToDateString(data.Timestamp)}`.toLowerCase();
			const assetInfo = `${data.AssetName + ' - ' + data.AssetDescription || ''}`.toLowerCase();
			const eventID = data.DeviationEventID.toString().toLowerCase();
			const tolerance = data.AcceptableDeviation.toString().toLowerCase();
			const sensitivity = data.Sensitivity.toString().toLowerCase();
			const percentage = data.PercentageDeviated.toString().toLowerCase();

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${assetInfo} ${eventID} ${tolerance} ${sensitivity} ${percentage}`;
			return combinedStr.includes(query);
		});
	}

	/**
	 * patchOldDistribution 
	 * @param distribution distribution 
	 * @param active active 
	 * @param disabled disabled 
	 */
	patchOldDistribution(distribution: AssetDistribution, active: boolean, disabled: boolean) {
		if (disabled && !confirm("Are you sure you want to delete this model?")) {
			return;
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.patchOldDistribution$(this.accountID, this.OrganisationUID, distribution.AssetDistributionID,
			this.asset.AssetID, active, disabled).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.getModel();
				})
			).subscribe({
				next: (result) => {
					if (result[0].Result.includes("OK")) {
						this.globalFunctions.DisplaySnackBar({
							message: active ? 'Model activated' : (!active && !disabled) ? 'Model deactivated' : disabled ? 'Model deleted' : '',
							type: SnackType.Info,
							duration: 1000
						});
						if (disabled) {
							this.goToModels();
						}
					}
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
			});
	}
}
